/**
 * GoogleConsentMode — Google Consent Mode v2 integration.
 *
 * Pushes default and update signals to window.dataLayer.
 * Must be called BEFORE the GTM container loads for defaults to take effect.
 */

import type { ConsentDecisions } from "../core/StorageAdapter";

type GcmStatus = "granted" | "denied";

interface GcmState {
  ad_storage: GcmStatus;
  ad_user_data: GcmStatus;
  ad_personalization: GcmStatus;
  analytics_storage: GcmStatus;
  functionality_storage: GcmStatus;
  personalization_storage: GcmStatus;
  security_storage: GcmStatus;
  wait_for_update?: number;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export class GoogleConsentMode {
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  /** Set GCM v2 defaults (denied for all non-essential) — call ASAP */
  setDefaults(): void {
    if (!this.enabled) return;
    this.ensureDataLayer();

    window.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      functionality_storage: "denied",
      personalization_storage: "denied",
      security_storage: "granted",
      wait_for_update: 500,
    } satisfies GcmState);
  }

  /** Update GCM v2 signals based on user's consent decisions */
  update(decisions: ConsentDecisions): void {
    if (!this.enabled) return;
    this.ensureDataLayer();

    const granted = (v: boolean): GcmStatus => (v ? "granted" : "denied");

    window.gtag("consent", "update", {
      ad_storage: granted(decisions.advertising),
      ad_user_data: granted(decisions.advertising),
      ad_personalization: granted(decisions.advertising),
      analytics_storage: granted(decisions.analytics),
      functionality_storage: granted(decisions.functional),
      personalization_storage: granted(decisions.functional),
      security_storage: "granted",
    } satisfies GcmState);

    // Push to dataLayer for GTM triggers
    window.dataLayer.push({
      event: "pb_consent_update",
      pb_analytics: decisions.analytics,
      pb_advertising: decisions.advertising,
      pb_functional: decisions.functional,
      pb_social: decisions.social,
    });
  }

  private ensureDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function (...args: unknown[]) {
        window.dataLayer.push(args);
      };
    }
  }
}
